import { Album, Close, OpenInNew, TheaterComedy } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
  linearProgressClasses,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

export interface ProxySession {
  id?: number;
  name?: string;
  true_user_id: string;
  proxy_user_id: string;
  proxy_prism_user_id: string;
  proxy_prism_client_id: string;
  prehire_flag: boolean;
  session_start: string;
  session_expiration: string;
}

export interface ProxyTimerProps {
  proxySession: ProxySession;
  onEndSession?: () => Promise<void>;
  onRedirect?: () => void;
  /** If true, shows a compact version without the end session button */
  compact?: boolean;
}

export const ProxyTimer: React.FC<ProxyTimerProps> = ({
  proxySession,
  onEndSession,
  onRedirect,
  compact = false,
}) => {
  const timesUp = useMemo(
    () => Date.parse(proxySession?.session_expiration!),
    [proxySession?.session_expiration]
  );
  const [time, setTime] = useState<number>(timesUp - Date.now());
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setTime(timesUp - Date.now()), 1000);

    return () => clearInterval(interval);
  }, [timesUp]);

  const handleDeactivateProxy = async () => {
    if (!onEndSession) return;
    try {
      setCancelLoading(true);
      await onEndSession();
      setCancelDialogOpen(false);
    } catch (error) {
      console.error('Failed to deactivate proxy:', error);
    } finally {
      setCancelLoading(false);
    }
  };

  useEffect(() => {
    if (time <= 0 && onEndSession) {
      handleDeactivateProxy();
    }
  }, [time]);

  const maxTime = useMemo(() => {
    const start = Date.parse(proxySession.session_start);
    const end = Date.parse(proxySession.session_expiration);
    return end - start;
  }, [proxySession.session_start, proxySession.session_expiration]);

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (!cancelLoading) {
      setCancelDialogOpen(false);
    }
  };

  if (time <= 0) return null;

  const progress = Math.min(100, Math.max(0, (time / maxTime) * 100));

  const gradient =
    time > 60000
      ? 'linear-gradient(90deg, #FC466B 0%, #3F5EFB 100%)'
      : '#ED2939';

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: compact ? 180 : 400,
          height: compact ? 32 : 40,
          display: 'flex',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Tooltip
          title={<ProxyInfoCard proxySession={proxySession} />}
          slotProps={{
            tooltip: { sx: { bgcolor: 'transparent' } },
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              px: compact ? 1.5 : 2,
              gap: compact ? 1 : 2,
              height: '100%',
              cursor: 'default',
              transition: 'background-color 0.2s',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TheaterComedy
                sx={{ fontSize: compact ? 16 : 18, color: 'text.secondary' }}
              />
              {!compact && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: 0.5,
                    fontSize: '0.75rem',
                  }}
                >
                  PROXY
                </Typography>
              )}
            </Box>

            <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  flex: 1,
                  height: compact ? 6 : 8,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  [`& .${linearProgressClasses.bar}`]: {
                    background: gradient,
                    borderRadius: 2,
                    transition: 'transform 0.2s linear',
                  },
                }}
              />
              <Album
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: `${progress}%`,
                  transform: 'translate(-50%, -50%)',
                  transition: 'transform 0.2s linear',
                  fontSize: compact ? 14 : 16,
                  color: time <= 60000 ? '#ED2939' : '#3F5EFB',
                }}
              />
            </Box>

            <Box
              sx={{
                width: compact ? 40 : 45,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: time <= 60000 ? '#ED2939' : 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: compact ? '0.8rem' : '0.9rem',
                  lineHeight: 1,
                }}
              >
                {time > 0
                  ? `${String(Math.floor(time / 60000)).padStart(
                      2,
                      '0'
                    )}:${String(Math.floor((time % 60000) / 1000)).padStart(
                      2,
                      '0'
                    )}`
                  : '00:00'}
              </Typography>
            </Box>
          </Box>
        </Tooltip>
        {!compact && onRedirect && (
          <>
            <Divider orientation="vertical" flexItem />

            <Tooltip title="Open Armhr">
              <IconButton
                size="small"
                onClick={onRedirect}
                sx={{
                  borderRadius: 0,
                  height: '100%',
                  width: 40,
                  color: 'text.secondary',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#1789FC',
                    color: 'error.contrastText',
                  },
                }}
              >
                <OpenInNew sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {!compact && onEndSession && (
          <>
            <Divider orientation="vertical" flexItem />

            <Tooltip title="End proxy session">
              <IconButton
                size="small"
                onClick={handleCancelClick}
                sx={{
                  borderRadius: 0,
                  height: '100%',
                  width: 40,
                  color: 'text.secondary',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                  },
                }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Paper>

      {onEndSession && (
        <Dialog
          open={cancelDialogOpen}
          onClose={handleDialogClose}
          aria-labelledby="cancel-proxy-dialog-title"
          aria-describedby="cancel-proxy-dialog-description"
        >
          <DialogTitle id="cancel-proxy-dialog-title">
            End Proxy Session
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="cancel-proxy-dialog-description">
              Are you sure you want to end the proxy session for{' '}
              <strong>{proxySession.proxy_prism_user_id}</strong> on client{' '}
              <strong>{proxySession.proxy_prism_client_id}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} disabled={cancelLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleDeactivateProxy}
              color="error"
              variant="contained"
              disabled={cancelLoading}
              startIcon={cancelLoading ? <CircularProgress size={16} /> : null}
            >
              {cancelLoading ? 'Ending...' : 'End Session'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const ProxyInfoCard: React.FC<{ proxySession: ProxySession }> = ({
  proxySession,
}) => {
  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <Paper
      sx={{
        px: 3,
        py: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TheaterComedy sx={{ color: '#3F5EFB' }} />
        <Typography variant="h6" color="#fff">
          Proxying As
        </Typography>
        {proxySession.prehire_flag && (
          <Chip
            label="Prehire"
            size="small"
            sx={{
              ml: 2,
              bgcolor: 'rgba(252, 70, 107, 0.2)',
              color: '#FC466B',
              borderColor: '#FC466B',
            }}
            variant="outlined"
          />
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 1 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <InfoRow label="Prism User" value={proxySession.proxy_prism_user_id} />
        <InfoRow label="Client ID" value={proxySession.proxy_prism_client_id} />
        <InfoRow
          label="Started"
          value={formatDateTime(proxySession.session_start)}
        />
        <InfoRow
          label="Expires"
          value={formatDateTime(proxySession.session_expiration)}
          color="#FC466B"
        />
      </Box>
    </Paper>
  );
};

const InfoRow: React.FC<{ label: string; value: string; color?: string }> = ({
  label,
  value,
  color = '#fff',
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
    <Typography variant="caption" color="rgba(255,255,255,0.6)">
      {label}
    </Typography>
    <Typography
      variant="caption"
      color={color}
      fontFamily="monospace"
      sx={{ wordBreak: 'break-all' }}
    >
      {value}
    </Typography>
  </Box>
);

export default ProxyTimer;
